import { ref, computed, Ref, ComputedRef, WritableComputedRef } from 'vue'
import { log } from '@/utils/DebugLogger'

export interface WizardStep {
  label?: string
  validate?: () => boolean
  onEnter?: () => void
  onLeave?: () => void
  [key: string]: any
}

export interface WizardProps {
  modelValue?: number
  steps?: WizardStep[]
  linear?: boolean
}

export type WizardEmitFn = (event: string, ...args: any[]) => void

export interface GoToStepOptions {
  force?: boolean
}

export function useWizard(props: WizardProps, emit: WizardEmitFn) {
  const internalCurrentStep: Ref<number> = ref(0)

  const currentStep: WritableComputedRef<number> = computed({
    get: () => (props.modelValue !== undefined ? props.modelValue : internalCurrentStep.value),
    set: (value: number) => {
      internalCurrentStep.value = value
      if (props.modelValue !== undefined) {
        emit('update:modelValue', value)
      }
    }
  })

  const totalSteps: ComputedRef<number> = computed(() => props.steps?.length || 0)

  const isFirstStep: ComputedRef<boolean> = computed(() => currentStep.value === 0)

  const isLastStep: ComputedRef<boolean> = computed(() => currentStep.value === totalSteps.value - 1)

  const currentStepConfig: ComputedRef<WizardStep | null> = computed(() => {
    if (!props.steps || currentStep.value >= props.steps.length) {
      return null
    }
    return props.steps[currentStep.value]
  })

  const validateCurrentStep = (): boolean => {
    const stepConfig = currentStepConfig.value

    if (!stepConfig) {
      log('useWizard', 'validateCurrentStep', 'No step config found')
      return false
    }

    if (typeof stepConfig.validate === 'function') {
      const isValid = stepConfig.validate()
      log('useWizard', 'validateCurrentStep', `Step ${currentStep.value} validation result:`, isValid)

      emit('step-validated', { step: currentStep.value, valid: isValid })

      if (!isValid) {
        emit('step-validation-failed', { step: currentStep.value })
      }

      return isValid
    }

    return true
  }

  const canNavigateToStep = (targetStep: number): boolean => {
    if (targetStep < 0 || targetStep >= totalSteps.value) {
      log('useWizard', 'canNavigateToStep', 'Invalid target step', targetStep)
      return false
    }

    if (targetStep < currentStep.value) {
      return true
    }

    if (!props.linear) {
      return true
    }

    if (targetStep > currentStep.value) {
      return validateCurrentStep()
    }

    return true
  }

  const goToStep = (targetStep: number, options: GoToStepOptions = {}): boolean => {
    const { force = false } = options
    log('useWizard', 'goToStep', 'Attempting navigation', { from: currentStep.value, to: targetStep, force })

    if (!force && !canNavigateToStep(targetStep)) {
      log('useWizard', 'goToStep', 'Navigation blocked by validation')
      return false
    }

    if (targetStep < 0 || targetStep >= totalSteps.value) {
      log('useWizard', 'goToStep', 'Invalid target step', targetStep)
      return false
    }

    const currentConfig = currentStepConfig.value
    if (currentConfig && typeof currentConfig.onLeave === 'function') {
      currentConfig.onLeave()
    }

    const beforeChangeEvent = { from: currentStep.value, to: targetStep, prevented: false }
    emit('before-step-change', beforeChangeEvent)

    if (beforeChangeEvent.prevented) {
      log('useWizard', 'goToStep', 'Navigation prevented by before-step-change event')
      return false
    }

    const oldStep = currentStep.value
    currentStep.value = targetStep

    const newConfig = props.steps?.[targetStep]
    if (newConfig && typeof newConfig.onEnter === 'function') {
      newConfig.onEnter()
    }

    emit('step-change', { from: oldStep, to: targetStep })

    log('useWizard', 'goToStep', 'Navigation successful', { from: oldStep, to: targetStep })
    return true
  }

  const nextStep = (): boolean => {
    log('useWizard', 'nextStep', 'Moving to next step')

    if (isLastStep.value) {
      log('useWizard', 'nextStep', 'Already on last step, calling finish')
      emit('finish')
      return false
    }

    if (!validateCurrentStep()) {
      log('useWizard', 'nextStep', 'Validation failed, cannot proceed')
      return false
    }

    emit('next', { currentStep: currentStep.value })
    return goToStep(currentStep.value + 1)
  }

  const previousStep = (): boolean => {
    log('useWizard', 'previousStep', 'Moving to previous step')

    if (isFirstStep.value) {
      log('useWizard', 'previousStep', 'Already on first step, calling cancel')
      emit('cancel')
      return false
    }

    emit('back', { currentStep: currentStep.value })
    return goToStep(currentStep.value - 1)
  }

  const reset = (): void => {
    log('useWizard', 'reset', 'Resetting wizard state')
    currentStep.value = 0
    emit('reset')
  }

  const complete = (data: any = null): void => {
    log('useWizard', 'complete', 'Completing wizard', data)
    emit('complete', data)
  }

  return {
    currentStep,
    totalSteps,
    isFirstStep,
    isLastStep,
    currentStepConfig,
    validateCurrentStep,
    canNavigateToStep,
    goToStep,
    nextStep,
    previousStep,
    reset,
    complete
  }
}
